import Config from '@/config'
import { parseQueryString } from '@/utils/query-string'
import {
  convertToYesNoValue,
  hashTextToSHA256,
  formatPaymentMethod,
} from '@/utils/tracking'

const ATTR_MAPPINGS = {
  event_label: ({ rootGetters }) => rootGetters['session/merchantId'],
  language: ({ rootGetters }) => rootGetters['application/currentLang'],
  logged_in: ({ state, rootGetters }) =>
    convertToYesNoValue(!!rootGetters['auth/loggedIn'] || state.loggedIn),
  id_verified: ({ state, rootGetters }) =>
    convertToYesNoValue(rootGetters['auth/isIdVerified'] || state.idVerified),
  customer_id: ({ rootGetters }) => rootGetters['auth/customerId'] || '',
  user_type: ({ rootGetters }) => rootGetters['auth/customerType'],
  sha256_email: ({ rootGetters }) =>
    hashTextToSHA256(rootGetters['auth/customerEmail']),
  sha256_phone_number: ({ rootGetters }) =>
    hashTextToSHA256(rootGetters['auth/customerPhoneNumber']),
  device_type: 'performance-issue-untrack',
  location_country: ({ rootGetters }) => rootGetters['session/countryCode'],
  page_type: ({ state }) => state.pageType,
  date_time: () => new Date().toISOString(),
  method_of_payment: ({ state }) => formatPaymentMethod(state.methodOfPayment),
  currency_code: ({ rootGetters }) => rootGetters['session/orderCurrency'],
  merchant_id: ({ rootGetters }) => rootGetters['session/merchantId'],
  merchant_name: ({ rootGetters }) => rootGetters['session/merchantName'],
  merchant_country: ({ rootGetters }) => rootGetters['session/merchantCountry'],
  payment_type: ({ rootGetters }) => {
    if (rootGetters['session/isPaylaterOrder']) {
      return 'pay_later'
    }

    if (rootGetters['session/isPayNextMonthOrder']) {
      return 'pay_next_month'
    }

    if (rootGetters['session/isPayNowOrder']) {
      return 'pay_now'
    }

    return `split_${rootGetters['session/numberOfInstallment']}`
  },
  order_total_amount: ({ rootGetters }) =>
    rootGetters['session/order']?.totalAmount?.amount || '',
  source: ({ state }) => state.source,
  medium: ({ state }) => state.medium,
  campaign: ({ state }) => state.campaign,
  order_id: ({ rootGetters }) => {
    const checkoutOrderId = rootGetters['checkout/orderId']

    if (checkoutOrderId) {
      return checkoutOrderId
    }

    // Get order id from URL
    const query = parseQueryString(window.location.search)
    const { orderId: orderIdInQuery } = query

    if (orderIdInQuery) {
      return orderIdInQuery
    }

    // Return empty if we don't know order id
    return ''
  },
  cc_available: ({ rootGetters }) =>
    convertToYesNoValue(rootGetters['payment/hasSavedCards']),
  channel: ({ rootGetters }) =>
    rootGetters['session/fromPaymentLink'] ? 'in-store' : 'online',
  is_referral: ({ state }) => convertToYesNoValue(state.isReferral),
  equal_installment: ({ rootGetters }) => {
    if (!rootGetters['session/isInstalmentOrder']) {
      return convertToYesNoValue(false)
    }

    const downpaymentAmount = rootGetters['checkout/downpayment']?.amount || 0
    const repayments = rootGetters['checkout/repayments']
    const installmentAmount = repayments[0]?.amount?.amount || 0
    const isEqual = Math.abs(downpaymentAmount - installmentAmount) <= 1

    return convertToYesNoValue(isEqual)
  },
  landing_variant: ({ rootState }) => {
    return rootState?.application?.landingVariant || 'NULL'
  },
  pay_the_difference_disclaimer: ({ rootState, rootGetters }) => {
    const isUnequalPayments =
      rootGetters['checkout/currentData'].isUnequalPayments
    if (isUnequalPayments) {
      return rootState?.application?.payTheDifferenceDisclaimer
    } else {
      return 'NULL'
    }
  },
  payment_types_details: ({ state }) => {
    if (Array.isArray(state.paymentTypesDetails)) {
      return state.paymentTypesDetails
    }

    return 'NULL'
  },
  session_id: ({ rootGetters }) => {
    return rootGetters.checkoutId
  },
  version: () => {
    return Config.env + '.' + Config.releaseVersion
  },
  platform: () => {
    return 'web'
  },
}

// Should only include global tracking attrs
const state = {
  loggedIn: '', // logged in status of the user
  idVerified: '', // id verification status of the user
  pageType: '', // page type
  methodOfPayment: '', // the method of payment selected by user
  source: '', // marketing source parameter (utm_source value in the url)
  medium: '', // marketing medium parameter (utm_medium value in the url)
  campaign: '', // marketing campaign parameter (utm_campaign value in the url)
  isReferral: '', // is a discount from referral applied to the total value
  paymentTypesDetails: '',
}

const getters = {
  getParameters: (state, getters, rootState, rootGetters) => (keys) => {
    return (keys || []).reduce((rs, key) => {
      const getterFn = ATTR_MAPPINGS[key] ?? 'N/A'
      const value = TypeChecker.isFunction(getterFn)
        ? getterFn({ state, getters, rootState, rootGetters })
        : getterFn

      return { ...rs, [key]: value }
    }, {})
  },
}

const mutations = {
  UPDATE_PARAMETERS(state, parameters = {}) {
    // XXX should we check key in initial state first?
    Object.entries(parameters).forEach(([key, value]) => {
      if (Config.isDev && !(key in state)) {
        console.error(
          `[Tracking Plugin] :: unknown key ${key} while updating tracking params`
        )
      }
      state[key] = value
    })
  },
}

const actions = {
  updateParameters({ commit }, parameters) {
    commit('UPDATE_PARAMETERS', parameters)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
